// src/pages/BasharNewsletterPage.jsx (o la ruta que corresponda)
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
    EnvelopeIcon, SparklesIcon, CheckCircleIcon,
    ArrowLeftIcon, ArrowRightIcon
} from '@heroicons/react/24/solid';
import UrlConfig from '../../data/UrlConfig'; // <<-- AJUSTA ESTA RUTA A TU ARCHIVO UrlConfig.js

const API_URLS = UrlConfig; // Asumiendo que UrlConfig es un objeto directamente o una función que devuelve un objeto.

const BasharNewsletterPage = () => {
    const [email, setEmail] = useState('');
    const [nick, setNick] = useState('');
    const [message, setMessage] = useState(''); // Para mensajes de éxito del backend
    const [error, setError] = useState('');     // Para mensajes de error del backend o frontend
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false); // Para controlar el estado de éxito en el frontend

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        setError('');

        // Validación básica en el frontend
        if (!nick.trim() || !email.trim()) {
            setError('Por favor, ingresa tu nombre/apodo y correo electrónico.');
            setIsLoading(false);
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Por favor, ingresa un correo electrónico válido.');
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch(API_URLS.leads_action, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email,
                    nick,
                    page: 'BasharNewsletterPage', // <<-- Aquí el valor específico para esta página
                    folder: 'bashar-following-transmission',
                    status: 'BasharInfo',
                    newsletter: ['bashar']
                }),
            });

            const data = await response.json();

            if (response.ok) {
                setMessage(data.message || '¡Gracias por suscribirte!'); // Usar mensaje del backend o predeterminado
                setEmail('');
                setNick('');
                setSubmitted(true); // Establecer como enviado para mostrar el mensaje de éxito
            } else {
                throw new Error(data.message || 'Ocurrió un error al procesar tu solicitud.');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full bg-gray-900 animate-fade-in min-h-screen flex flex-col justify-between">
            <div className="max-w-3xl mx-auto py-12 px-4 sm:px-6 lg:px-8 flex-grow flex items-center"> {/* Centrar el contenido verticalmente */}
                <div className="bg-gray-900 p-8 rounded-xl border border-purple-500/20 shadow-lg w-full max-w-xl mx-auto">
                    <div className="text-center mb-6">
                        <EnvelopeIcon className="w-16 h-16 text-purple-400 mx-auto mb-3 animate-bounce-slow" />
                        <h1 className="text-3xl font-bold text-purple-300 mb-2">¡No te Pierdas la Siguiente Transmisión de Bashar!</h1>
                        <p className="text-gray-300 text-lg">
                            Suscríbete a nuestra newsletter para ser el primero en enterarte cuando la próxima transmisión esté disponible. ¡Recibe actualizaciones, insights exclusivos y más!
                        </p>
                    </div>

                    {submitted ? (
                        <div className="text-center text-green-400 p-6 bg-green-900/30 border border-green-700 rounded-lg">
                            <CheckCircleIcon className="w-12 h-12 mx-auto mb-3 text-green-400" />
                            <p className="text-xl font-semibold">{message}</p>
                            <p className="text-lg">Te notificaremos cuando la siguiente transmisión de Bashar esté lista.</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label htmlFor="nick" className="block text-gray-200 text-sm font-semibold mb-2">Nombre o Apodo:</label>
                                <input
                                    type="text"
                                    id="nick"
                                    className="w-full p-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="Tu nombre o apodo (ej. Arthur Dent)"
                                    value={nick}
                                    onChange={(e) => setNick(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-gray-200 text-sm font-semibold mb-2">Correo Electrónico:</label>
                                <input
                                    type="email"
                                    id="email"
                                    className="w-full p-3 bg-gray-800 text-white border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    placeholder="tu.email@ejemplo.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                            </div>

                            {(error || message) && (
                                <p className={`text-sm text-center ${error ? 'text-red-400' : 'text-green-400'}`}>
                                    {error || message}
                                </p>
                            )}

                            <button
                                type="submit"
                                className="w-full inline-flex items-center justify-center gap-2 bg-purple-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-purple-500 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        Unirme a la Newsletter <SparklesIcon className="w-5 h-5" />
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>

            {/* Navigation links at the bottom */}
            <div className="text-center py-6 px-4 sm:px-6 lg:px-8 bg-gray-900 border-t border-gray-700">
                <Link to="/cursos/manifiesta" className="text-sm text-gray-500 hover:text-yellow-400 transition-colors">Volver al Temario</Link>
            </div>
        </div>
    );
};

export default BasharNewsletterPage;